import type { GoalInterface } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';
import type { AdExchangeType } from 'sonata-static-storage/lib/schemas/targeting/AdExchange/AdExchange.types';

export const getVariableSelectorOptions = (
  iabLevels: { [p: string]: any[] },
  formatMessage: any,
  levelToExclude?: string
) => {
  const options = [
    {
      id: 'level0',
      label: formatMessage({
        defaultMessage: 'Categories',
        id: 'IABCategoriesSelector.categories'
      })
    },
    {
      id: 'level1',
      label: formatMessage({
        defaultMessage: 'Subcategories',
        id: 'IABCategoriesSelector.subcategories'
      })
    },
    {
      id: 'level2',
      label: formatMessage({
        defaultMessage: 'Subcategories 2',
        id: 'IABCategoriesSelector.subcategories2'
      })
    },
    {
      id: 'level3',
      label: formatMessage({
        defaultMessage: 'Subcategories 3',
        id: 'IABCategoriesSelector.subcategories3'
      })
    },
    {
      id: 'level4',
      label: formatMessage({
        defaultMessage: 'Subcategories 4',
        id: 'IABCategoriesSelector.subcategories4'
      })
    }
  ];

  const levelsSize = Object.keys(iabLevels).length;
  const newOptions = options.slice(0, levelsSize);
  const itemToExclude = newOptions.find(item => item.id === levelToExclude);
  if (itemToExclude) {
    const itemIndex = newOptions.indexOf(itemToExclude);
    return newOptions.slice(0, itemIndex);
  }
  return newOptions;
};

export const getFlightsTotalGoals = (flightsGoals: GoalInterface[]) => {
  const goals = flightsGoals.reduce<{
    [type in GoalInterface['type']]: {
      [timeFrame in GoalInterface['timeFrame']]: GoalInterface[];
    };
  }>(
    (goalsByType, goal) => {
      if (goal.target) {
        goalsByType[goal.type][goal.timeFrame].push(goal);
      }
      return goalsByType;
    },
    {
      audio_complete: {
        daily: [],
        overall: []
      },
      audio_start: {
        daily: [],
        overall: []
      },
      budget: {
        daily: [],
        overall: []
      },
      clicks: {
        daily: [],
        overall: []
      },
      //@ts-ignore
      engagements: {
        daily: [],
        overall: []
      },
      impressions: {
        daily: [],
        overall: []
      },
      pages_viewed: {
        daily: [],
        overall: []
      },
      playouts: {
        daily: [],
        overall: []
      },
      store_visits: {
        daily: [],
        overall: []
      },
      video_complete: {
        daily: [],
        overall: []
      },
      video_start: {
        daily: [],
        overall: []
      }
    }
  );

  return Object.entries(goals).map<{
    daily: number;
    name: GoalInterface['type'];
    overall: number;
  }>(([name, goalTotals]) => ({
    daily: goalTotals.daily.reduce((sum: number, goal: any) => sum + goal.target, 0),
    name: name as GoalInterface['type'],
    overall: goalTotals.overall.reduce((sum: number, goal: any) => sum + goal.target, 0)
  }));
};

export const MAX_AD_EXCHANGE_DISTRIBUTION = 100;

export const getAdExchangesTotalDistribution = (
  adExchanges: AdExchangeType[],
  updatedAd?: {
    distribution: number;
    name: string;
  }
): number =>
  adExchanges.reduce(
    (acc, adExchange) =>
      acc + (adExchange.name === updatedAd?.name ? updatedAd.distribution! : adExchange.distribution),
    0
  );

const incrementAdExchanges = (
  adExchanges: AdExchangeType[],
  incrementMultiplier: number,
  startingTotal: number,
  updatedAd?: {
    distribution: number;
    name: string;
  }
): {
  currentDistribution: number;
  updatedAds: AdExchangeType[];
} =>
  adExchanges.reduce<{
    currentDistribution: number;
    updatedAds: AdExchangeType[];
  }>(
    (acc, ad) => {
      if (updatedAd?.name === ad.name) {
        return {
          ...acc,
          updatedAds: [
            ...acc.updatedAds,
            {
              ...ad,
              distribution: updatedAd!.distribution
            }
          ]
        };
      }
      if (ad.blacklisted || acc.currentDistribution === MAX_AD_EXCHANGE_DISTRIBUTION) {
        return {
          ...acc,
          updatedAds: [...acc.updatedAds, ad]
        };
      }
      const distribution = Math.max(ad.distribution + incrementMultiplier, 0);
      const incrementDiff = distribution - ad.distribution;
      return {
        currentDistribution: acc.currentDistribution + incrementDiff,
        updatedAds: [
          ...acc.updatedAds,
          {
            ...ad,
            distribution
          }
        ]
      };
    },
    {
      currentDistribution: startingTotal,
      updatedAds: []
    }
  );

export const equalizeAdExchangesDistribution = (
  adExchanges: AdExchangeType[],
  updatedAd?: {
    distribution: number;
    name: string;
  }
): AdExchangeType[] => {
  const totalDistribution = getAdExchangesTotalDistribution(adExchanges, updatedAd);
  const incrementMultiplier =
    totalDistribution === MAX_AD_EXCHANGE_DISTRIBUTION ? 0 : totalDistribution > MAX_AD_EXCHANGE_DISTRIBUTION ? -1 : 1;

  const sortedAds = adExchanges.sort(
    (adA, adB) => (adA.distribution > adB.distribution ? 1 : -1) * incrementMultiplier
  );
  let { currentDistribution, updatedAds } = incrementAdExchanges(
    sortedAds,
    incrementMultiplier,
    totalDistribution,
    updatedAd
  );

  while (currentDistribution !== MAX_AD_EXCHANGE_DISTRIBUTION) {
    const { currentDistribution: newDistributions, updatedAds: newAds } = incrementAdExchanges(
      updatedAds,
      incrementMultiplier,
      currentDistribution,
      updatedAd
    );
    if (newDistributions === currentDistribution) {
      break;
    }
    currentDistribution = newDistributions;
    updatedAds = newAds;
  }
  return updatedAds;
};
